/**
 * 🧠 G2GENTE - Avaliação Big Five (OCEAN)
 * - Versão BFI-44 (44 perguntas) ou NEO-PI-R (60 perguntas)
 * - Auto-teste informal (3 dias) ou oficial (6 meses)
 * - Gráfico radar dos 5 traços
 * - Embaralhamento de questões para testes subsequentes
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { candidatesAPI, testInvitesAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft,
  Loader2,
  Brain,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Users,
  Heart,
  Shield,
  Sparkles,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Info,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

// ═══════════════════════════════════════════════════════════════════════════════
// BFI-44: 44 PERGUNTAS DO BIG FIVE INVENTORY
// ═══════════════════════════════════════════════════════════════════════════════
// Cada pergunta tem: id, texto, traço (O/C/E/A/N), e se é reversa (pontuação invertida)

const BIG_FIVE_QUESTIONS_44 = [
  // ═══════════════════════════════════════════════════════════════════════════
  // EXTROVERSÃO (E) - 8 perguntas
  // ═══════════════════════════════════════════════════════════════════════════
  { id: 1, text: "É comunicativo, conversador", trait: "E", reverse: false },
  { id: 6, text: "É reservado", trait: "E", reverse: true },
  { id: 11, text: "É cheio de energia", trait: "E", reverse: false },
  { id: 16, text: "Gera muito entusiasmo", trait: "E", reverse: false },
  { id: 21, text: "Tende a ser quieto, calado", trait: "E", reverse: true },
  { id: 26, text: "Tem uma personalidade assertiva", trait: "E", reverse: false },
  { id: 31, text: "É, às vezes, tímido e inibido", trait: "E", reverse: true },
  { id: 36, text: "É extrovertido, sociável", trait: "E", reverse: false },

  // ═══════════════════════════════════════════════════════════════════════════
  // AMABILIDADE (A) - 9 perguntas
  // ═══════════════════════════════════════════════════════════════════════════
  { id: 2, text: "Tende a encontrar defeitos nos outros", trait: "A", reverse: true },
  { id: 7, text: "É prestativo e ajuda os outros", trait: "A", reverse: false },
  { id: 12, text: "Inicia brigas e discussões com os outros", trait: "A", reverse: true },
  { id: 17, text: "Tem capacidade de perdoar", trait: "A", reverse: false },
  { id: 22, text: "É geralmente confiável", trait: "A", reverse: false },
  { id: 27, text: "Pode ser frio e distante", trait: "A", reverse: true },
  { id: 32, text: "É atencioso e gentil com quase todos", trait: "A", reverse: false },
  { id: 37, text: "É, às vezes, rude e grosseiro com os outros", trait: "A", reverse: true },
  { id: 42, text: "Gosta de cooperar com os outros", trait: "A", reverse: false },

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSCIENCIOSIDADE (C) - 9 perguntas
  // ═══════════════════════════════════════════════════════════════════════════
  { id: 3, text: "Faz um trabalho completo e detalhado", trait: "C", reverse: false },
  { id: 8, text: "Pode ser um pouco descuidado", trait: "C", reverse: true },
  { id: 13, text: "É um trabalhador de confiança", trait: "C", reverse: false },
  { id: 18, text: "Tende a ser desorganizado", trait: "C", reverse: true },
  { id: 23, text: "Tende a ser preguiçoso", trait: "C", reverse: true },
  { id: 28, text: "Persiste até terminar a tarefa", trait: "C", reverse: false },
  { id: 33, text: "Faz as coisas de forma eficiente", trait: "C", reverse: false },
  { id: 38, text: "Faz planos e os segue à risca", trait: "C", reverse: false },
  { id: 43, text: "Se distrai facilmente", trait: "C", reverse: true },

  // ═══════════════════════════════════════════════════════════════════════════
  // NEUROTICISMO (N) → Exibido como "Estabilidade Emocional" (invertido)
  // ═══════════════════════════════════════════════════════════════════════════
  { id: 4, text: "É deprimido, melancólico", trait: "N", reverse: false },
  { id: 9, text: "É relaxado, lida bem com estresse", trait: "N", reverse: true },
  { id: 14, text: "Pode ficar tenso", trait: "N", reverse: false },
  { id: 19, text: "Se preocupa muito", trait: "N", reverse: false },
  { id: 24, text: "É emocionalmente estável, não se altera facilmente", trait: "N", reverse: true },
  { id: 29, text: "Pode ser temperamental, de humor instável", trait: "N", reverse: false },
  { id: 34, text: "Permanece calmo em situações tensas", trait: "N", reverse: true },
  { id: 39, text: "Fica nervoso facilmente", trait: "N", reverse: false },

  // ═══════════════════════════════════════════════════════════════════════════
  // ABERTURA (O) - 10 perguntas
  // ═══════════════════════════════════════════════════════════════════════════
  { id: 5, text: "É original, tem novas ideias", trait: "O", reverse: false },
  { id: 10, text: "É curioso sobre muitas coisas diferentes", trait: "O", reverse: false },
  { id: 15, text: "É engenhoso, um pensador profundo", trait: "O", reverse: false },
  { id: 20, text: "Tem uma imaginação ativa", trait: "O", reverse: false },
  { id: 25, text: "É inventivo, criativo", trait: "O", reverse: false },
  { id: 30, text: "Valoriza experiências artísticas e estéticas", trait: "O", reverse: false },
  { id: 35, text: "Prefere trabalho que seja rotineiro", trait: "O", reverse: true },
  { id: 40, text: "Gosta de refletir, brincar com ideias", trait: "O", reverse: false },
  { id: 41, text: "Tem poucos interesses artísticos", trait: "O", reverse: true },
  { id: 44, text: "É sofisticado em arte, música ou literatura", trait: "O", reverse: false }
];

// Versão estendida NEO-PI-R (60 perguntas) - adiciona 16 perguntas extras
const BIG_FIVE_QUESTIONS_60 = [
  ...BIG_FIVE_QUESTIONS_44,
  // Perguntas adicionais para maior precisão
  // Extroversão
  { id: 45, text: "Gosta de estar no centro das atenções", trait: "E", reverse: false },
  { id: 46, text: "Prefere atividades solitárias", trait: "E", reverse: true },
  { id: 47, text: "Sente-se confortável em grupos grandes", trait: "E", reverse: false },
  // Amabilidade
  { id: 48, text: "Acredita no melhor das pessoas", trait: "A", reverse: false },
  { id: 49, text: "Às vezes é desconfiado das intenções dos outros", trait: "A", reverse: true },
  { id: 50, text: "Coloca as necessidades dos outros antes das suas", trait: "A", reverse: false },
  // Conscienciosidade
  { id: 51, text: "Sempre cumpre prazos", trait: "C", reverse: false },
  { id: 52, text: "Às vezes deixa tarefas pela metade", trait: "C", reverse: true },
  { id: 53, text: "É meticuloso com detalhes", trait: "C", reverse: false },
  // Neuroticismo
  { id: 54, text: "Raramente se sente triste ou deprimido", trait: "N", reverse: true },
  { id: 55, text: "Frequentemente se sente ansioso", trait: "N", reverse: false },
  { id: 56, text: "Lida bem com críticas", trait: "N", reverse: true },
  // Abertura
  { id: 57, text: "Está sempre buscando aprender coisas novas", trait: "O", reverse: false },
  { id: 58, text: "Prefere o familiar ao desconhecido", trait: "O", reverse: true },
  { id: 59, text: "Gosta de desafiar ideias estabelecidas", trait: "O", reverse: false },
  { id: 60, text: "Aprecia diferentes culturas e perspectivas", trait: "O", reverse: false }
];

const LIKERT_SCALE = [
  { value: 1, label: "Discordo totalmente" },
  { value: 2, label: "Discordo parcialmente" },
  { value: 3, label: "Neutro" },
  { value: 4, label: "Concordo parcialmente" },
  { value: 5, label: "Concordo totalmente" }
];

// Perfis dos 5 traços
const BIG_FIVE_TRAITS = {
  O: {
    name: "Abertura",
    fullName: "Abertura a Experiências",
    icon: Lightbulb,
    color: "text-purple-500",
    bgColor: "bg-purple-100",
    chartColor: "#a855f7",
    highDesc: "Criativo, curioso, aprecia arte e novas experiências. Pensa de forma original e busca inovação.",
    lowDesc: "Prático, convencional, prefere rotinas estabelecidas. Focado no concreto e tradicional.",
    careerHigh: ["Criação", "P&D", "Design", "Consultoria", "Startups"],
    careerLow: ["Operações", "Compliance", "Controle de Qualidade"]
  },
  C: {
    name: "Conscienciosidade",
    fullName: "Conscienciosidade",
    icon: Shield,
    color: "text-blue-500",
    bgColor: "bg-blue-100",
    chartColor: "#3b82f6",
    highDesc: "Organizado, disciplinado, confiável e focado em objetivos. Planeja com antecedência e cumpre compromissos.",
    lowDesc: "Flexível, espontâneo, adapta-se facilmente. Pode ter dificuldade com rotinas rígidas.",
    careerHigh: ["Gestão", "Finanças", "Engenharia", "Direito", "Medicina"],
    careerLow: ["Ambientes muito criativos sem estrutura"]
  },
  E: {
    name: "Extroversão",
    fullName: "Extroversão",
    icon: Users,
    color: "text-amber-500",
    bgColor: "bg-amber-100",
    chartColor: "#f59e0b",
    highDesc: "Sociável, energético, assertivo. Gosta de interagir com pessoas e ambientes dinâmicos.",
    lowDesc: "Reservado, introspectivo, prefere ambientes calmos. Recarrega energia em momentos de solidão.",
    careerHigh: ["Vendas", "Marketing", "Liderança", "Eventos", "Atendimento"],
    careerLow: ["Pesquisa individual", "Programação", "Análise de dados"]
  },
  A: {
    name: "Amabilidade",
    fullName: "Amabilidade",
    icon: Heart,
    color: "text-rose-500",
    bgColor: "bg-rose-100",
    chartColor: "#f43f5e",
    highDesc: "Cooperativo, empático, confiável. Valoriza harmonia e se preocupa com o bem-estar dos outros.",
    lowDesc: "Competitivo, direto, questionador. Foca em resultados e pode ser cético.",
    careerHigh: ["RH", "Saúde", "Educação", "Serviço Social", "Suporte"],
    careerLow: ["Negociações agressivas", "Cobrança"]
  },
  N: {
    name: "Estabilidade Emocional",
    fullName: "Estabilidade Emocional",
    icon: Sparkles,
    color: "text-emerald-500",
    bgColor: "bg-emerald-100",
    chartColor: "#10b981",
    // Nota: Neuroticismo é invertido, então "high" aqui significa BAIXO neuroticismo
    highDesc: "Calmo, resiliente, emocionalmente estável. Lida bem com pressão e mantém o equilíbrio.",
    lowDesc: "Sensível, reativo a estresse. Beneficia-se de ambientes estruturados e previsíveis.",
    careerHigh: ["Emergências", "Alta pressão", "Liderança em crise", "Cirurgia"],
    careerLow: ["Ambientes muito caóticos sem suporte"]
  }
};

// Ordem dos traços para exibição
const TRAIT_ORDER = ['O', 'C', 'E', 'A', 'N'];
const TRAIT_LABELS = ['Abertura', 'Conscienciosidade', 'Extroversão', 'Amabilidade', 'Estabilidade'];

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE: Gráfico Radar Big Five
// ═══════════════════════════════════════════════════════════════════════════════
function BigFiveRadarChart({ scores }) {
  const traits = TRAIT_ORDER;
  const labels = TRAIT_LABELS;
  const colors = traits.map(t => BIG_FIVE_TRAITS[t].chartColor);
  
  const centerX = 150;
  const centerY = 150;
  const radius = 100;
  
  // Normalizar scores (0-100) para (0-1)
  const normalizedScores = traits.map(t => (scores[t] || 50) / 100);
  
  // Calcular pontos do polígono
  const points = normalizedScores.map((score, i) => {
    const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
    const x = centerX + Math.cos(angle) * radius * score;
    const y = centerY + Math.sin(angle) * radius * score;
    return `${x},${y}`;
  }).join(' ');
  
  const gridLevels = [0.25, 0.5, 0.75, 1];
  
  return (
    <div className="w-full flex justify-center" data-testid="bigfive-radar-chart">
      <svg viewBox="0 0 300 300" className="w-full max-w-[280px]">
        {/* Grid */}
        {gridLevels.map((level, i) => (
          <polygon
            key={i}
            points={traits.map((_, j) => {
              const angle = (Math.PI * 2 * j) / traits.length - Math.PI / 2;
              const x = centerX + Math.cos(angle) * radius * level;
              const y = centerY + Math.sin(angle) * radius * level;
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth="1"
          />
        ))}
        
        {/* Eixos */}
        {traits.map((_, i) => {
          const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;
          return (
            <line
              key={i}
              x1={centerX}
              y1={centerY}
              x2={x}
              y2={y}
              stroke="#cbd5e1"
              strokeWidth="1"
            />
          );
        })}
        
        {/* Área preenchida */}
        <polygon
          points={points}
          fill="rgba(139, 92, 246, 0.25)"
          stroke="#8b5cf6"
          strokeWidth="2.5"
        />
        
        {/* Pontos */}
        {normalizedScores.map((score, i) => {
          const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
          const x = centerX + Math.cos(angle) * radius * score;
          const y = centerY + Math.sin(angle) * radius * score;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="6"
              fill={colors[i]}
              stroke="white"
              strokeWidth="2"
            />
          );
        })}
        
        {/* Labels */}
        {labels.map((label, i) => {
          const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
          const labelRadius = radius + 32;
          const x = centerX + Math.cos(angle) * labelRadius;
          const y = centerY + Math.sin(angle) * labelRadius;
          const percentage = Math.round(normalizedScores[i] * 100);
          
          return (
            <g key={i}>
              <text
                x={x}
                y={y - 6}
                textAnchor="middle"
                className="text-[10px] font-semibold"
                fill={colors[i]}
              >
                {label.substring(0, 4)}
              </text>
              <text
                x={x}
                y={y + 8}
                textAnchor="middle"
                className="text-[11px] font-bold"
                fill="#374151"
              >
                {percentage}%
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE: Barras de cada traço
// ═══════════════════════════════════════════════════════════════════════════════
function BigFiveBars({ scores }) {
  const traits = TRAIT_ORDER.map(key => ({
    key,
    ...BIG_FIVE_TRAITS[key]
  }));
  
  return (
    <div className="space-y-3" data-testid="bigfive-bars">
      {traits.map(({ key, name, color, bgColor }) => {
        const percentage = Math.round(scores[key] || 50);
        const colorClass = color.replace('text-', 'bg-');
        return (
          <div key={key}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-600">{name}</span>
              <span className="font-semibold text-slate-900">{percentage}%</span>
            </div>
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className={`h-full ${colorClass} rounded-full transition-all duration-500`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export default function MobileBigFive() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Parâmetros de URL para teste oficial (solicitado por empresa)
  const isOfficialRequest = searchParams.get('oficial') === 'true';
  const requestedVersion = searchParams.get('versao') || 'BFI-44';
  const jobId = searchParams.get('job_id');
  const companyName = searchParams.get('empresa');
  
  const [loading, setLoading] = useState(true);
  const [existingResult, setExistingResult] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [isRetaking, setIsRetaking] = useState(false);
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Usar versão 44 ou 60 perguntas dependendo da solicitação
  const [questions, setQuestions] = useState([]);
  const [questionOrder, setQuestionOrder] = useState([]);
  const [testVersion, setTestVersion] = useState('BFI-44');

  // Carregar dados existentes
  useEffect(() => {
    const loadExistingBigFive = async () => {
      try {
        const response = await candidatesAPI.getMe();
        const candidate = response.data;
        
        // Se é solicitação oficial, verificar se tem Big Five oficial válido
        if (isOfficialRequest) {
          setTestVersion(requestedVersion);
          
          // Se tem resultado OFICIAL válido, mostrar
          if (candidate.bigfive_result && candidate.bigfive_updated_at) {
            const bigfiveTipo = candidate.bigfive_result.tipo;
            const expiresAt = new Date(candidate.bigfive_expires_at || new Date());
            const now = new Date();
            
            // Só considerar válido se for oficial e não expirado
            if (bigfiveTipo === 'oficial' && now <= expiresAt) {
              const diffTime = expiresAt - now;
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              setDaysRemaining(diffDays);
              
              setExistingResult({
                scores: candidate.bigfive_result.scores || {},
                tipo: 'oficial',
                versao: candidate.bigfive_result.versao || 'BFI-44',
                updatedAt: candidate.bigfive_updated_at,
                expiresAt: candidate.bigfive_expires_at
              });
            } else {
              // Não tem oficial válido, vai precisar fazer
              setIsExpired(true);
            }
          }
        } else {
          // Auto-teste: mostrar qualquer resultado existente
          if (candidate.bigfive_result && candidate.bigfive_updated_at) {
            const lastUpdate = new Date(candidate.bigfive_updated_at);
            const expiresAt = new Date(candidate.bigfive_expires_at || lastUpdate);
            const now = new Date();
            
            if (now > expiresAt) {
              setIsExpired(true);
            } else {
              const diffTime = expiresAt - now;
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              setDaysRemaining(diffDays);
            }
            
            setExistingResult({
              scores: candidate.bigfive_result.scores || {},
              tipo: candidate.bigfive_result.tipo || 'informal',
              versao: candidate.bigfive_result.versao || 'BFI-44',
              updatedAt: candidate.bigfive_updated_at,
              expiresAt: candidate.bigfive_expires_at
            });
          }
        }
      } catch (error) {
        console.error('Erro ao carregar Big Five:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadExistingBigFive();
  }, [isOfficialRequest, requestedVersion]);

  // Preparar questões (embaralhar se não for primeira vez)
  useEffect(() => {
    const prepareQuestions = () => {
      // Escolher versão correta
      let questionsToUse = testVersion === 'NEO-PI-R' 
        ? [...BIG_FIVE_QUESTIONS_60] 
        : [...BIG_FIVE_QUESTIONS_44];
      
      // Sempre embaralhar para testes oficiais, ou se já fez antes
      if (isOfficialRequest || existingResult || isRetaking) {
        // Fisher-Yates shuffle
        for (let i = questionsToUse.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [questionsToUse[i], questionsToUse[j]] = [questionsToUse[j], questionsToUse[i]];
        }
      }
      
      setQuestions(questionsToUse);
      setQuestionOrder(questionsToUse.map(q => q.id));
    };
    
    if (!loading) {
      prepareQuestions();
    }
  }, [loading, existingResult, isRetaking, testVersion, isOfficialRequest]);

  const totalQuestions = questions.length;
  const progress = totalQuestions > 0 ? ((currentQuestion + (result ? 1 : 0)) / totalQuestions) * 100 : 0;
  const currentAnswer = answers[currentQuestion];

  const handleSelectAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion]: value });
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResult();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateResult = () => {
    // Calcular scores para cada traço
    const traitScores = { O: [], C: [], E: [], A: [], N: [] };
    
    questions.forEach((q, index) => {
      let score = answers[index] || 3;
      
      // Inverter se for pergunta reversa
      if (q.reverse) {
        score = 6 - score; // 1->5, 2->4, 3->3, 4->2, 5->1
      }
      
      traitScores[q.trait].push(score);
    });
    
    // Calcular média para cada traço (convertido para 0-100%)
    const scores = {};
    Object.keys(traitScores).forEach(trait => {
      const traitAnswers = traitScores[trait];
      if (traitAnswers.length > 0) {
        const avg = traitAnswers.reduce((a, b) => a + b, 0) / traitAnswers.length;
        // Converter de escala 1-5 para 0-100%
        scores[trait] = Math.round(((avg - 1) / 4) * 100);
      } else {
        scores[trait] = 50;
      }
    });
    
    // Inverter Neuroticismo para Estabilidade Emocional
    scores.N = 100 - scores.N;
    
    // Encontrar traço dominante
    const sortedTraits = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const dominant = sortedTraits[0][0];
    const secondary = sortedTraits[1][0];
    
    // Definir tipo baseado se é oficial ou informal
    const tipo = isOfficialRequest ? 'oficial' : 'informal';
    const versao = testVersion;
    
    setResult({
      scores,
      dominant,
      secondary,
      tipo,
      versao
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const now = new Date();
      // 6 meses para oficial, 3 dias para informal
      const expirationDays = isOfficialRequest ? 180 : 3;
      const expiresAt = new Date(now.getTime() + expirationDays * 24 * 60 * 60 * 1000);
      
      const bigfiveData = {
        scores: result.scores,
        tipo: result.tipo,
        versao: result.versao,
        dominant: result.dominant,
        secondary: result.secondary,
        questoes_ordem: questionOrder,
        // Se oficial, guardar info da vaga
        ...(isOfficialRequest && {
          job_id: jobId,
          company_name: companyName,
          requested_at: now.toISOString()
        })
      };
      
      await candidatesAPI.updateMe({ 
        bigfive_result: bigfiveData,
        bigfive_updated_at: now.toISOString(),
        bigfive_expires_at: expiresAt.toISOString()
      });
      
      // Verificar se veio de um convite de teste e marcar como respondido
      const inviteToken = sessionStorage.getItem('test_invite_token');
      if (inviteToken) {
        try {
          await testInvitesAPI.complete(inviteToken);
          sessionStorage.removeItem('test_invite_token');
          toast.success('Teste Big Five completado com sucesso! A empresa foi notificada.');
        } catch (inviteError) {
          console.error('Erro ao completar convite:', inviteError);
          // Não bloquear o fluxo se falhar a atualização do convite
        }
      } else if (isOfficialRequest) {
        toast.success('Avaliação Big Five oficial salva! Válida por 6 meses.');
      } else {
        toast.success('Avaliação Big Five salva com sucesso!');
      }
      navigate('/m/profile');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar avaliação');
    } finally {
      setSaving(false);
    }
  };

  const handleStartRetake = () => {
    setIsRetaking(true);
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Mostrar resultado existente
  if (existingResult && !isRetaking && !result && !isExpired) {
    const dominant = BIG_FIVE_TRAITS[existingResult.scores ? 
      Object.entries(existingResult.scores).sort((a, b) => b[1] - a[1])[0][0] : 'O'];
    const DominantIcon = dominant.icon;

    return (
      <div className="min-h-screen bg-slate-50 pb-24" data-testid="bigfive-existing-result">
        {/* Header */}
        <div className="bg-gradient-to-br from-purple-900 to-purple-800 px-4 pt-4 pb-20 rounded-b-3xl">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate('/m')} className="text-purple-300">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold text-white">Seu Perfil Big Five</h1>
            {existingResult.tipo === 'informal' && (
              <span className="ml-auto bg-amber-500/20 text-amber-300 text-xs px-2 py-1 rounded-full">
                Teste Informal
              </span>
            )}
          </div>

          <div className="text-center">
            <div className={`w-20 h-20 ${dominant.bgColor} rounded-full flex items-center justify-center mx-auto mb-3`}>
              <DominantIcon className={`w-10 h-10 ${dominant.color}`} />
            </div>
            <h2 className="text-xl font-bold text-white">Traço Dominante</h2>
            <p className={`text-lg font-semibold ${dominant.color}`}>{dominant.fullName}</p>
            <div className="mt-3 flex items-center justify-center gap-2 text-purple-300 text-xs">
              <Clock className="w-4 h-4" />
              <span>Avaliado em {formatDate(existingResult.updatedAt)}</span>
            </div>
            {existingResult.tipo === 'informal' && (
              <div className="mt-2 flex items-center justify-center gap-2 text-amber-300 text-xs">
                <AlertTriangle className="w-4 h-4" />
                <span>Expira em {daysRemaining} dia{daysRemaining > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </div>

        <div className="px-4 -mt-12 space-y-4">
          {/* Gráfico Radar */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-4 text-center">Gráfico do Perfil</h3>
              <BigFiveRadarChart scores={existingResult.scores} />
            </CardContent>
          </Card>

          {/* Barras */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Detalhamento por Traço</h3>
              <BigFiveBars scores={existingResult.scores} />
            </CardContent>
          </Card>

          {/* Info sobre teste informal */}
          {existingResult.tipo === 'informal' && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-1">Teste Informal</h3>
                    <p className="text-sm text-amber-700">
                      Este resultado é para seu autoconhecimento. Quando uma empresa solicitar 
                      o Big Five oficial, você fará uma nova avaliação com perguntas embaralhadas.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Descrições dos traços */}
          {TRAIT_ORDER.map(key => {
            const trait = BIG_FIVE_TRAITS[key];
            const score = existingResult.scores[key] || 50;
            const isHigh = score >= 60;
            const TraitIcon = trait.icon;
            
            return (
              <Card key={key} className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 ${trait.bgColor} rounded-full flex items-center justify-center`}>
                      <TraitIcon className={`w-5 h-5 ${trait.color}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{trait.fullName}</h3>
                      <span className={`text-sm ${trait.color} font-medium`}>{score}%</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {isHigh ? trait.highDesc : trait.lowDesc}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Botão de Refazer */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
          <Button 
            className="w-full bg-purple-600 hover:bg-purple-700"
            onClick={handleStartRetake}
            data-testid="bigfive-retake-btn"
          >
            <Brain className="w-5 h-5 mr-2" />
            Refazer Avaliação
          </Button>
        </div>
      </div>
    );
  }

  // Tela de resultado após questionário
  if (result) {
    const dominant = BIG_FIVE_TRAITS[result.dominant];
    const secondary = BIG_FIVE_TRAITS[result.secondary];
    const DominantIcon = dominant.icon;

    return (
      <div className="min-h-screen bg-slate-50 pb-24" data-testid="bigfive-result">
        <div className="bg-gradient-to-br from-purple-900 to-purple-800 px-4 pt-4 pb-20 rounded-b-3xl">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate('/m')} className="text-purple-300">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold text-white">Seu Resultado Big Five</h1>
            <CheckCircle2 className="w-5 h-5 text-emerald-400 ml-auto" />
          </div>

          <div className="text-center">
            <div className={`w-24 h-24 ${dominant.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <DominantIcon className={`w-12 h-12 ${dominant.color}`} />
            </div>
            <h2 className="text-2xl font-bold text-white">{dominant.fullName}</h2>
            <p className={`text-lg ${dominant.color}`}>Traço Dominante</p>
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="text-purple-300 text-sm">Secundário:</span>
              <span className={`${secondary.color} font-medium`}>{secondary.name}</span>
            </div>
          </div>
        </div>

        <div className="px-4 -mt-12 space-y-4">
          {/* Gráfico */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-4 text-center">Gráfico do Perfil</h3>
              <BigFiveRadarChart scores={result.scores} />
            </CardContent>
          </Card>

          {/* Barras */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Detalhamento por Traço</h3>
              <BigFiveBars scores={result.scores} />
            </CardContent>
          </Card>

          {/* Descrição do dominante */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-2">Sobre seu perfil</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                {result.scores[result.dominant] >= 60 ? dominant.highDesc : dominant.lowDesc}
              </p>
            </CardContent>
          </Card>

          {/* Carreiras sugeridas */}
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <h3 className="font-semibold text-purple-900 mb-3">Áreas que combinam com você</h3>
              <div className="flex flex-wrap gap-2">
                {(result.scores[result.dominant] >= 60 ? dominant.careerHigh : dominant.careerLow).map((career, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700"
                  >
                    {career}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Aviso sobre teste informal */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-1">Teste Informal</h3>
                  <p className="text-sm text-amber-700">
                    Este resultado expira em 3 dias e serve para seu autoconhecimento. 
                    Quando uma empresa solicitar, você fará o teste oficial.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
          <Button 
            className="w-full bg-purple-600 hover:bg-purple-700"
            onClick={handleSave}
            disabled={saving}
            data-testid="bigfive-save-btn"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
            {saving ? 'Salvando...' : 'Salvar Resultado'}
          </Button>
        </div>
      </div>
    );
  }

  // Tela inicial (sem resultado ou expirado)
  if (!existingResult || isExpired || isRetaking) {
    // Se ainda não tem questões carregadas
    if (questions.length === 0) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      );
    }

    const question = questions[currentQuestion];
    if (!question) {
      return null;
    }

    return (
      <div className="min-h-screen bg-slate-50" data-testid="bigfive-quiz">
        {/* Header */}
        <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
          <div className="flex items-center gap-3 p-4">
            <button onClick={() => navigate('/m')} className="text-slate-600">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-slate-900">Big Five</h1>
              <p className="text-xs text-slate-500">Pergunta {currentQuestion + 1} de {totalQuestions}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Brain className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <Progress value={progress} className="h-1.5" />
          
          {/* Navigation Buttons - Moved to top */}
          <div className="flex gap-2 px-4 py-2 bg-slate-50 border-t border-slate-100">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className="flex-1 h-9"
              data-testid="bigfive-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            <Button
              size="sm"
              onClick={handleNext}
              disabled={currentAnswer === undefined}
              className="flex-1 h-9 bg-purple-600 hover:bg-purple-700"
              data-testid="bigfive-next-btn"
            >
              {currentQuestion === totalQuestions - 1 ? 'Ver Resultado' : 'Próximo'}
              {currentQuestion < totalQuestions - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>

        {/* Instrução inicial */}
        {currentQuestion === 0 && (
          <>
            {/* Banner de solicitação oficial */}
            {isOfficialRequest && (
              <div className="mx-4 mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-semibold text-sm text-blue-900">Avaliação Oficial</p>
                    <p className="text-xs text-blue-700">
                      Solicitada por <strong>{companyName || 'Empresa'}</strong> • Válida por 6 meses
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mx-4 mt-3 p-3 bg-purple-50 border border-purple-200 rounded-xl">
              <p className="text-xs text-purple-800 text-center">
                <strong>Instruções:</strong> Responda o quanto cada afirmação descreve você. 
                Não há respostas certas ou erradas.
              </p>
            </div>
          </>
        )}

        {/* Pergunta */}
        <div className="p-4">
          <Card className="border-slate-200 mb-4">
            <CardContent className="p-4">
              <p className="text-center text-slate-500 text-xs mb-1">Eu me vejo como alguém que...</p>
              <p className="text-base font-medium text-slate-900 text-center leading-relaxed">
                "{question.text}"
              </p>
            </CardContent>
          </Card>

          {/* Escala Likert - Compact version */}
          <div className="space-y-2">
            {LIKERT_SCALE.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelectAnswer(option.value)}
                className={`w-full p-3 rounded-xl text-left transition-all ${
                  currentAnswer === option.value
                    ? 'bg-purple-600 text-white shadow-lg scale-[1.02]'
                    : 'bg-white border border-slate-200 hover:border-purple-500 hover:bg-purple-50'
                }`}
                data-testid={`bigfive-likert-${option.value}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    currentAnswer === option.value
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {option.value}
                  </div>
                  <span className={`text-sm font-medium ${
                    currentAnswer === option.value ? 'text-white' : 'text-slate-900'
                  }`}>
                    {option.label}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Progress indicator */}
          <p className="text-center text-xs text-slate-400 mt-4">
            {Object.keys(answers).length} de {totalQuestions} respondidas
          </p>
        </div>
      </div>
    );
  }

  return null;
}
